import type { SideBarOptionTypes } from './types'
import type { MouseEventHandler } from 'react'

import cx from 'classnames'
import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { MdChatBubble, MdCheckBox, MdDragIndicator, MdEditDocument, MdFeedback, MdNotifications, MdPageview, MdReport, MdSettings, MdSpaceDashboard, MdWatchLater } from 'react-icons/md'
import { RiFolderTransferFill } from 'react-icons/ri'
import { useLocation, useParams } from 'react-router-dom'
import { animated, useSpring } from 'react-spring'

import { notify } from '../../../lib/Notification/actions'
import * as passive from '../../../lib/passive-events'
import { feedback } from '../../../lib/sentry'
import { hasAdminRole } from '../../../selectors/user'
import { useAppDispatch, useAppSelector } from '../../../store'
import * as sidebarActions from '../../../store/modules/sidebar'
import { useChatsMetaQuery } from '../../chats'
import { useAlerts } from '../../dashboard/queries/alerts'
import { getStatus } from '../../LoginForm/actions'
import { Divider } from '../../shared/Divider'
import { useSidebar } from '../context'

import { Alerts } from './alerts'
import { SidebarChats } from './chats'
import * as SidebarComponents from './components/index'
import { EncounterSummary } from './encounter-summary'
import { Notifications } from './notifications/notifications'
import { OpenEncounters } from './open-encounters/open-encounters'
import { PatientSearch } from './patient-search'
import { RecentPatients } from './recent-patient'
import styles from './sidebar.module.css'
import { Tasks } from './tasks'
import { SidebarOptionKind } from './types'
import { extract, User, UserIcon } from './user/user'

const WHITE_COMPONENTS = [ SidebarOptionKind.messages ]

let separator: SideBarOptionTypes = {
  name: SidebarOptionKind.separator,
  link: null,
  icon: <Divider style={{ background: '#C2C2C2', width: '100%' }} />
}

let recentPatients: SideBarOptionTypes = {
  name: SidebarOptionKind.recent_patients,
  id: 'recent-patients',
  link: '/',
  tooltip: 'Recent Patients',
  icon: (
    <MdWatchLater />
  )
}

let openEncounters: SideBarOptionTypes = {
  name: SidebarOptionKind.encounters,
  id: 'open-encounters',
  tooltip: 'Open Encounters',
  icon: (
    <RiFolderTransferFill />
  )
}

let search: SideBarOptionTypes = {
  name: SidebarOptionKind.patient_search,
  id: 'patient-search',
  tooltip: 'Patient Search',
  icon: (
    <MdPageview />
  )
}

let tasks: SideBarOptionTypes = {
  name: SidebarOptionKind.tasks,
  id: 'tasks',
  tooltip: 'Tasks',
  icon: (
    <MdCheckBox />
  )
}

let notification: SideBarOptionTypes = {
  name: SidebarOptionKind.notification,
  id: 'notification',
  tooltip: 'Notifications',
  icon: (
    <MdNotifications />
  )
}

let adminPage: SideBarOptionTypes = {
  name: SidebarOptionKind.admin_page,
  id: 'admin-page',
  link: '/admin',
  tooltip: 'Open Admin Page',
  icon: (
    <MdSettings />
  ),
  isLink: true
}

let encounterSummary: SideBarOptionTypes = {
  name: SidebarOptionKind.encounter_summary,
  id: 'encounter-summary',
  tooltip: 'Encounter Summary',
  icon: (
    <MdEditDocument />
  )
}

let dashboard: SideBarOptionTypes = {
  name: SidebarOptionKind.dashboard,
  id: 'dashboard',
  link: '/',
  tooltip: 'Dashboard',
  icon: (
    <MdSpaceDashboard />
  ),
  isLink: true
}

interface CopyableProps extends React.HTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
  value: string
}

export function Copyable ({ value, children, className, onClick, ...rest }: CopyableProps): JSX.Element {
  let dispatch = useAppDispatch()

  let handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(event => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        dispatch(notify('Copied to clipboard', false))
      })
      .catch(err => {
        console.error(err)
      })
    onClick?.(event)
  }, [ value, onClick, dispatch ])

  return (
    <button
      type='button'
      {...rest}
      className={cx(styles.copyable, className)}
      onClick={handleClick}
    >
      {children}
    </button>
  )
}

export function Sidebar (): JSX.Element {
  let { state, close, setState } = useSidebar()
  let stateRef = useRef(state)

  useEffect(() => {
    const element = document.querySelector('#sentry-user-feedback')
    element && feedback.attachTo(element)
    return () => {
      feedback.remove()
    }
  }, [])

  let dispatch = useAppDispatch()

  let view = useAppSelector(state => state.sidebar.view)
  let encounterId = useAppSelector(state => state.encounter?.encounter?.id)
  let viewRef = useRef(view)

  let { status, profile, encounter } = useAppSelector((state) => ({
    profile: state.auth.profile,
    status: state.auth.status,
    encounter: state.encounter?.encounter
  }))

  let chatsMetadata = useChatsMetaQuery({ userId: profile?.sub ?? undefined })

  let [ isAdmin, mobileManagement ] = useAppSelector((state) => [
    hasAdminRole(state),
    state.globalVars?.permissions?.mobile_management
  ])

  let params = useParams()
  let location = useLocation()

  let showEncounterSummary = !!(
    params.id &&
      params.clientName &&
      location.pathname.includes('assessment') &&
    (encounter?.document?.type === 'cms' ||
      encounter?.document?.type === 'patient')
  )

  const id = profile?.sub as string ?? ''
  const { value: onDuty } = extract(id, status, 'onduty')

  let user: SideBarOptionTypes = useMemo(() => ({
    name: SidebarOptionKind.user,
    id: 'user',
    tooltip: 'User Menu',
    link: null,
    icon: (
      <UserIcon />
    )
  }), [])

  const handleClickUserInfo = useCallback((): void => {
    const url = document.location.href
    const value = `URL: ${url}
under ${profile?.given_name ?? 'Unknown given name'} ${profile?.family_name ?? 'Unknown family name'} 
at ${new Date().toLocaleString()}
User ID: ${profile?.sub ?? 'Unknown id'}
Version: ${window.constants.COMMIT?.slice(0, 7) ?? 'unknown-commit'}`

    navigator.clipboard
      .writeText(value)
      .then(() => toast('Copied to clipboard', { position: 'bottom-center', duration: 4000 }))
      .catch(err => console.error(err))
  }, [ profile?.family_name, profile?.given_name, profile?.sub ])

  let userInfo: SideBarOptionTypes = useMemo(() => ({
    name: SidebarOptionKind.user_info,
    id: 'user-info',
    tooltip: 'User Info',
    // style: { alignItems: 'end', flex: 1 },
    fn: handleClickUserInfo,
    isButton: true,
    icon: profile
      ? (
        <div style={{ color: '#b4b4b4' }}>
          {profile?.given_name?.substring(0, 1)}{profile?.family_name?.substring(0, 1)}
        </div>
      )
      : undefined
  }), [ handleClickUserInfo, profile ])

  let sentryUserFeedback = useMemo<SideBarOptionTypes>(() => ({
    name: SidebarOptionKind.sentry_user_feedback,
    id: 'sentry-user-feedback',
    tooltip: 'Send feedback',
    style: { alignItems: 'end', flex: 1 },
    fn: () => ({}),
    isButton: true,
    icon: (
      <div style={{ color: '#b4b4b4' }}>
        <MdFeedback />
      </div>
    )
  }), [])

  let alerts: SideBarOptionTypes = useMemo(() => ({
    name: SidebarOptionKind.alerts,
    id: 'alerts',
    tooltip: 'Open Alerts page',
    icon: <AlertsIcon />
  }), [])

  let messages: SideBarOptionTypes = useMemo(() => {
    const ownStats = chatsMetadata.data?.chats?.own

    // const ownStats = { unreadCount: 100 }

    return ({
      name: SidebarOptionKind.messages,
      tooltip: 'Messages',
      icon: (
        <div className='relative'>
          <MdChatBubble />
          {(ownStats?.unreadCount ?? 0) > 0 && <div className='absolute -top-1.5 left-3 bg-[#EBF0FC] text-[#3464E3] rounded-full text-xs font-semibold  px-1 flex items-center justify-center'>{ownStats?.unreadCount ?? 0}</div>}
        </div>
      )
    })
  }, [ chatsMetadata.data?.chats?.own ])

  function handleClick (name: SidebarOptionKind): void {
    if (state === name) {
      close()
    } else {
      setState(name)
    }
  }

  let showAdminTab = isAdmin || [ 'read', 'write' ].includes(mobileManagement)

  let sidebar: SideBarOptionTypes [] = useMemo(() => {
    const baseItems = [
      user,
      search,
      openEncounters,
      recentPatients,
      notification,
      tasks,
      alerts,
      separator,
      dashboard,
      messages
    ]

    if (showAdminTab) {
      baseItems.push(adminPage)
    }

    if (showEncounterSummary) {
      baseItems.push(encounterSummary)
    }

    if (profile) {
      baseItems.push(sentryUserFeedback)
      baseItems.push(userInfo)
    }

    return baseItems
  }, [ user, alerts, messages, showAdminTab, showEncounterSummary, profile, sentryUserFeedback, userInfo ])

  useEffect(() => {
    if (!showEncounterSummary && state === SidebarOptionKind.encounter_summary) {
      close()
    }
  }, [ close, showEncounterSummary, state ])

  useEffect(() => {
    dispatch(getStatus())
  }, [ dispatch ])

  useEffect(() => {
    let hasChanged = state !== stateRef.current

    if (!hasChanged) {
      return
    }

    stateRef.current = state

    if (state === SidebarOptionKind.encounter_summary && !showEncounterSummary) {
      close()
      return
    }

    if (state === null || state === 'closed') {
      dispatch(sidebarActions.close())
      return
    }

    dispatch(sidebarActions.open(state))
  }, [ dispatch, close, showEncounterSummary, state, view ])

  useEffect(() => {
    let hasChanged = viewRef.current !== view

    if (!hasChanged) {
      return
    }

    viewRef.current = view

    if (view === SidebarOptionKind.encounter_summary && !showEncounterSummary) {
      close()
      return
    }

    if (typeof view === 'undefined') {
      close()
      return
    }

    setState(view)
  }, [ close, setState, showEncounterSummary, state, view ])

  let content: JSX.Element | undefined
  let contentRef = useRef<JSX.Element | undefined>()

  switch (state) {
    case SidebarOptionKind.user:
      content = <User />
      break
    case SidebarOptionKind.recent_patients:
      content = <RecentPatients />
      break
    case SidebarOptionKind.encounters:
      content = <OpenEncounters />
      break
    case SidebarOptionKind.patient_search:
      content = <PatientSearch />
      break
    case SidebarOptionKind.notification:
      content = <Notifications />
      break
    case SidebarOptionKind.encounter_summary:
      // TODO: Without this, the old draft state is preserved in the encounter
      // summary between encounters. This is a hack to force a re-render of the
      // component.
      content = <EncounterSummary key={encounterId} />
      break
    case SidebarOptionKind.tasks:
      content = <Tasks />
      break
    case SidebarOptionKind.messages:
      content = <SidebarChats />
      break
    case SidebarOptionKind.alerts:
      content = <Alerts />
      break
  }

  if (content) {
    contentRef.current = content
  }

  let minWidth = 250
  let [ maxWidth, setMaxWidth ] = useState(600)

  let defaultWidth = 300

  let initialWidth = useMemo(() => {
    let value = localStorage.getItem(`user:${id}:sidebar:width`)
    let width: number | undefined

    try {
      width = parseInt(value ?? '', 10)
    } catch (err) {}

    return typeof width === 'number'
      ? isNaN(width)
        ? defaultWidth
        : width ?? defaultWidth
      : defaultWidth
  }, [ id, defaultWidth ])

  let [ width, setWidth ] = useState(initialWidth)
  let widthRef = useRef(initialWidth)

  let [ isAnimating, setIsAnimating ] = useState(false)
  let [ isDragging, setIsDragging ] = useState(false)

  let [ style, api ] = useSpring({
    width: state && state !== 'closed'
      ? `${width - 58}px`
      : '0px',

    onStart () {
      setIsAnimating(true)
    },

    onRest () {
      setIsAnimating(false)
    }
  }, [ state ])

  let handleMouseDown = useCallback(() => {
    setIsDragging(true)
  }, [])

  let handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  let handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging) {
      return
    }

    let width = Math.max(minWidth ?? 0, Math.min(maxWidth ?? Infinity, widthRef.current - event.movementX))

    setWidth(width)

    api.set({
      width: `${width - 58}px`
    })
  }, [ isDragging, maxWidth, minWidth, api ])

  let handleWindowResize = useCallback(() => {
    setMaxWidth(document.body.clientWidth / 2 - 32)
  }, [])

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp, passive.options)
    window.addEventListener('mousemove', handleMouseMove, passive.options)

    return () => {
      window.removeEventListener('mouseup', handleMouseUp, passive.options)
      window.removeEventListener('mousemove', handleMouseMove, passive.options)
    }
  }, [ handleMouseUp, handleMouseMove ])

  useEffect(() => {
    handleWindowResize()
    window.addEventListener('resize', handleWindowResize, passive.options)

    return () => {
      window.removeEventListener('resize', handleWindowResize, passive.options)
    }
  }, [ handleWindowResize ])

  useEffect(() => {
    if (!isDragging) {
      return
    }

    let values: Partial<CSSStyleDeclaration> = {
      userSelect: 'none',
      cursor: 'grabbing'
    }

    let previous = Object.keys(values).reduce((acc, key) => ({ ...acc, [key]: document.body.style[key as keyof CSSStyleDeclaration] }), {})
    Object.assign(document.body.style, values)

    return () => {
      Object.assign(document.body.style, previous)
    }
  }, [ isDragging ])

  useEffect(() => {
    widthRef.current = width
  }, [ width ])

  useEffect(() => {
    if (!isDragging) {
      localStorage.setItem(`user:${id}:sidebar:width`, width.toString(10))
    }
  }, [ isDragging, width, id ])

  return (
    <>
      <div className='flex flex-row relative'>
        <button
          type='button'
          onMouseDown={handleMouseDown}
          className={cx(
            'tailwind h-[100vh] w-4 flex flex-col justify-center items-center transition-all ease-in-out duration-200 hover:bg-gray-50',
            (!state || state === 'closed') && 'opacity-0 pointer-events-none ',
            (state && state !== 'closed') && 'opacity-100 pointer-events-auto',
            isDragging && 'cursor-grabbing bg-gray-50',
            !isDragging && 'cursor-grab'
          )}
        >
          <div
            className={cx(
              'py-0.5 rounded opacity-50 hover:opacity-80',
              isDragging && 'opacity-100'
            )}
          >
            <MdDragIndicator
              size={18}
              className='text-gray-400'
            />
          </div>
        </button>

        <div
          id='the-sidebar'
          className={styles.sidebarContainer}
        >
          <SidebarComponents.Sidebar
            onDuty={onDuty}
            onClick={handleClick}
            selected={state}
            items={sidebar}
          />

          <animated.div
            className={cx(styles.containerWrapper, isAnimating && 'overflow-hidden', 'flex-1', state && WHITE_COMPONENTS.includes(state as SidebarOptionKind) ? 'bg-white' : 'bg-[#f7f7f7]')}
            style={style}
          >
            <div
              className='flex flex-col flex-grow'
              style={{
                width: isAnimating ? `${width - 58}px` : undefined
              }}
            >
              {content ?? contentRef.current}
            </div>

            {(state !== 'closed' && state) && (
              <>
                {state !== SidebarOptionKind.messages && (
                  <div className={styles.spacer} />
                )}

                {/* <Info /> */}
              </>
            )}
          </animated.div>
        </div>
      </div>
    </>
  )
}

export function AlertsIcon (): JSX.Element {
  const alertsQuery = useAlerts()

  const length = alertsQuery?.data?.length ?? 0

  return (

    <div className='relative'>
      <MdReport />
      {length > 0 && (
        <div
          className='absolute -top-1.5 left-3 bg-[#EBF0FC] text-[#3464E3] rounded-full text-xs font-semibold  px-1 flex items-center justify-center'
        >
          {length}
        </div>
      )}
    </div>
  )
}
